import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fcfa } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CountrySelect, CountryNotice } from "@/components/CountrySelect";

const Field = ({ id, label, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={`checkout-${id}`} {...p} /></div>
);

const Payments = ({ methods }) => (
  <div className="space-y-2">
    {!methods.some((m) => m.enabled) && <p data-testid="payment-none" className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">Aucun moyen de paiement n'est encore disponible pour ce pays.</p>}
    {methods.map((m) => (
      <div key={m.code} data-testid={`payment-${m.code}`} className={`flex items-center justify-between rounded-xl border p-3 text-sm ${m.enabled ? "border-orange-400 bg-orange-50" : "opacity-60"}`}>
        <span className="font-semibold">{m.name}</span>
        {m.enabled ? <span className="text-xs font-bold text-orange-700">Sélectionné</span> : <span className="flex items-center gap-1 text-xs text-gray-500"><Lock className="h-3 w-3" />Bientôt disponible</span>}
      </div>
    ))}
  </div>
);

export default function Checkout() {
  const { items, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", address: "", city: "Dakar", country_code: user?.country_code || "SN" });
  const [shops, setShops] = useState({});
  const [zones, setZones] = useState({});
  const [methods, setMethods] = useState([]);
  const [busy, setBusy] = useState(false);
  const groups = useMemo(() => items.reduce((g, i) => ({ ...g, [i.shop_id]: [...(g[i.shop_id] || []), i] }), {}), [items]);

  useEffect(() => {
    api.get(`/payments/providers?country=${form.country_code}`).then((r) => setMethods(r.data));
  }, [form.country_code]);
  useEffect(() => {
    api.get("/shops").then((r) => setShops(Object.fromEntries(r.data.map((s) => [s.id, s]))));
  }, []);

  const currency = items[0]?.currency || "XOF";
  const zonesOf = (sid) => (shops[sid]?.delivery?.zones || []).filter((z) => (z.country_code || shops[sid].country_code) === form.country_code);
  const fee = (sid) => zonesOf(sid).find((z) => z.name === zones[sid])?.fee || 0;
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const total = subtotal + Object.keys(groups).reduce((s, sid) => s + fee(sid), 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.variant })),
        delivery: form, zones, payment_method: "paiement_livraison",
      });
      clear();
      toast.success(`${data.orders.length} commande(s) enregistrée(s)`);
      nav(data.orders.length === 1 ? `/mes-commandes/${data.orders[0].id}` : "/mes-commandes");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  if (!items.length) return <p className="p-10 text-center text-gray-500">Votre panier est vide.</p>;
  return (
    <form onSubmit={submit} data-testid="checkout-page" className="mx-auto grid max-w-6xl gap-8 px-4 py-8 md:grid-cols-[1fr_360px] md:px-6">
      <div className="space-y-8">
        <h1 className="font-display text-3xl font-black tracking-tight">Finaliser la commande</h1>
        <section className="grid gap-4 rounded-2xl border bg-white p-5 sm:grid-cols-2">
          <h2 className="font-display text-lg font-bold sm:col-span-2">Informations de livraison</h2>
          <Field id="name" label="Nom complet" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field id="phone" label="Téléphone" required placeholder="+221 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field id="address" label="Adresse" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <Field id="city" label="Ville" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <div className="space-y-1.5 sm:col-span-2"><Label>Pays de livraison</Label>
            <CountrySelect testid="checkout-country" need="delivery" value={form.country_code} onChange={(v) => { setForm({ ...form, country_code: v }); setZones({}); }} />
            <CountryNotice code={form.country_code} need="delivery" />
          </div>
        </section>
        {Object.entries(groups).map(([sid, list]) => (
          <section key={sid} className="space-y-3 rounded-2xl border bg-white p-5">
            <h2 className="font-display text-lg font-bold">{list[0].shop_name}</h2>
            {list.map((i) => <p key={i.product_id + i.variant} className="flex justify-between text-sm"><span>{i.quantity} × {i.title}{i.variant ? ` (${i.variant})` : ""}</span><span className="tnum">{fcfa(i.price * i.quantity, currency)}</span></p>)}
            {shops[sid] && shops[sid].country_code !== form.country_code && <p data-testid={`shop-country-warning-${sid}`} className="text-xs text-amber-700">Cette boutique livre uniquement dans son pays pour le moment.</p>}
            {zonesOf(sid).length > 0 && (
              <Select value={zones[sid] || ""} onValueChange={(v) => setZones({ ...zones, [sid]: v })}>
                <SelectTrigger data-testid={`zone-select-${sid}`}><SelectValue placeholder="Choisir la zone de livraison" /></SelectTrigger>
                <SelectContent>{zonesOf(sid).map((z) => <SelectItem key={z.name} value={z.name}>{z.name} · {fcfa(z.fee, currency)} · {z.delay}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </section>
        ))}
      </div>
      <aside className="h-fit space-y-4 rounded-2xl border bg-white p-5 md:sticky md:top-24">
        <h2 className="font-display text-xl font-bold">Paiement</h2>
        <Payments methods={methods} />
        <p className="text-xs text-gray-500">Aucun paiement en ligne n'est effectué. Vous réglez le vendeur à la réception.</p>
        <div className="space-y-1 border-t pt-3 text-sm">
          <p className="flex justify-between"><span>Sous-total</span><span className="tnum">{fcfa(subtotal, currency)}</span></p>
          <p className="flex justify-between"><span>Livraison</span><span className="tnum">{fcfa(total - subtotal, currency)}</span></p>
          <p className="flex justify-between text-base font-bold"><span>Total</span><span data-testid="checkout-total" className="tnum text-orange-600">{fcfa(total, currency)}</span></p>
        </div>
        <Button type="submit" size="lg" className="w-full rounded-full" disabled={busy} data-testid="checkout-submit">{busy ? "Envoi…" : "Confirmer la commande"}</Button>
      </aside>
    </form>
  );
}
